import React, { useContext } from "react";
import AvatarContato from "../AvatarContato";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay, differenceInHours, differenceInMinutes } from "date-fns";

import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import Checkbox from "@material-ui/core/Checkbox";


import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import SimCardIcon from "@material-ui/icons/SimCard";
import PersonIcon from "@material-ui/icons/Person";
import PhotoIcon from "@material-ui/icons/Photo";
import VideocamIcon from "@material-ui/icons/Videocam";
import MicIcon from "@material-ui/icons/Mic";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import EmojiEmotionsIcon from "@material-ui/icons/EmojiEmotions";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import { AuthContext } from "../../context/Auth/AuthContext";
import useTagVisivel from "../../hooks/useTagVisivel";
import { nomeArquivo } from "../../helpers/legendaMidia";
import { ehVCard, lerVCards, descreverContatos } from "../../helpers/vcard";

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
		// Dentro do .ticketRow (flex com o checkbox), o ListItem é um flex item.
		// Sem minWidth 0 ele não encolhe abaixo do próprio min-content (nome +
		// metadados sem quebra) e transborda a lista, jogando hora e badge de
		// não lidas pra fora da área visível.
		flex: "1 1 auto",
		minWidth: 0,
	},

	ticketRow: {
		display: "flex",
		alignItems: "center",
		width: "100%",
		boxSizing: "border-box",
		transition: "background-color 120ms ease",
	},

	// Linha marcada na múltipla seleção: o destaque fica na linha inteira, não só
	// no quadradinho — dá pra bater o olho e ver o que vai entrar na ação em lote.
	ticketRowSelecionada: {
		backgroundColor: "var(--status-blue-bg)",
	},

	// Coluna do checkbox com largura fixa: sem ela o avatar dançava alguns pixels
	// entre uma linha e outra.
	selectCheckboxCell: {
		flex: "none",
		width: 32,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},

	selectCheckbox: {
		padding: 4,
	},

	pendingTicket: {
		cursor: "unset",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
		minWidth: 0,
	},

	// Bloco "nome + conexão + atendente": encolhe livremente (minWidth 0) para
	// nunca empurrar a hora pra fora. Cada parte tem seu próprio "...".
	contactNameBlock: {
		display: "flex",
		alignItems: "center",
		minWidth: 0,
		overflow: "hidden",
	},

	// Nome do contato e conexão não encolhem (flexShrink 0): qualquer encolhida,
	// mesmo de fração de pixel, já dispara o "..." e come letras à toa. O
	// maxWidth 100% garante o "..." quando o texto sozinho passa da linha.
	contactName: {
		flexShrink: 0,
		minWidth: 0,
		maxWidth: "100%",
	},

	// Sem fontSize próprio: o tamanho vem do variant="body2" do Typography, igual
	// era quando nome/conexão/atendente eram um único Typography.
	contactMeta: {
		flexShrink: 0,
		minWidth: 0,
		maxWidth: "100%",
	},

	// Único item elástico da linha: o nome do atendente absorve toda a falta de
	// espaço (e trunca com "...") antes de nome do contato e conexão sofrerem.
	contactMetaUser: {
		flexShrink: 1,
	},

	lastMessageTime: {
		flexShrink: 0,
		whiteSpace: "nowrap",
		marginLeft: 4,
	},

	// Mesma escala dos ícones da linha de cima (o tema usa fontSize 10).
	lastMessageMediaIcon: {
		fontSize: 12,
		verticalAlign: "middle",
		margin: "0 3px 2px 0",
		color: theme.palette.text.secondary,
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 32,
		marginLeft: "auto",
	},

	contactLastMessage: {
		paddingRight: 20,
		minWidth: 0,
	},

	newMessagesCount: {
		alignSelf: "center",
		marginRight: 12,
		marginLeft: "auto",
		flexShrink: 0,
	},

	badgeStyle: {
		color: "white",
		backgroundColor: green[500],
	},

	// Calha dos marcadores (etapa / etiqueta). Eram duas barras de 8px colando na
	// borda e ocupando a altura inteira da linha; agora são pílulas finas
	// arredondadas, com respiro em cima e embaixo. O flex resolve a posição: com um
	// marcador só, ele ocupa a calha sem deixar buraco de barra ausente.
	// Cabe dentro do padding lateral do ListItem (16px): nada de empurrar o avatar
	// nem de precisar competir com o padding do MUI.
	marcadores: {
		position: "absolute",
		left: 4,
		top: 8,
		bottom: 8,
		display: "flex",
		alignItems: "stretch",
		gap: 3,
	},

	marcador: {
		width: 4,
		borderRadius: 4,
	},

	// Sem etapa nem etiqueta (aba Aguardando) a calha ficava vazia e a linha
	// começava solta na borda — este trilho neutro mantém a divisão da lateral.
	marcadorTrilho: {
		backgroundColor: "var(--border)",
	},

	userTag: {
		position: "absolute",
		marginRight: 5,
		right: 5,
		bottom: 5,
		backgroundColor: theme.palette.background.default,
		color: theme.palette.primary,
		border: "1px solid var(--border)",
		padding: 1,
		paddingLeft: 5,
		paddingRight: 5,
		borderRadius: 10,
		fontSize: "1em"
	},

	assignedToMeCorner: {
		position: "absolute",
		right: 0,
		bottom: 0,
		width: 0,
		height: 0,
		borderStyle: "solid",
		borderWidth: "0 0 25px 25px",
		borderColor: `transparent transparent ${theme.palette.primary.main} transparent`,
		pointerEvents: "none",
		zIndex: 1,
		pointerEvents: "auto",
	},
	assignedToMeCornerWhite: {
		position: "absolute",
		right: 0,
		bottom: -25,
		width: 0,
		height: 0,
		borderStyle: "solid",
		borderWidth: "0 0 10px 10px",
		// Fundo da "fita" = superfície do card (--surface-1): branco no claro,
		// #111b21 no escuro. Antes era "white" fixo e vazava no dark mode.
		borderColor: `transparent transparent ${theme.palette.background.paper} transparent`,
		pointerEvents: "none",
		zIndex: 2,
		pointerEvents: "auto",
	},
	titleTooltip: {
		fontSize: '30px !important'
	},
	inlineMetaIcon: {
		fontSize: 12,
		verticalAlign: "middle",
		margin: "0 3px 2px 5px",
		color: theme.palette.text.secondary,
	},
	inlineMetaIconWhats: {
		fontSize: 12,
		verticalAlign: "middle",
		margin: "0 3px 2px 5px",
		color: theme.palette.text.secondary,
	}
}));

const ICONES_MIDIA = {
	image: PhotoIcon,
	video: VideocamIcon,
	audio: MicIcon,
	document: InsertDriveFileIcon,
	sticker: EmojiEmotionsIcon,
	contact: AccountCircleIcon,
};

const ROTULOS_MIDIA = {
	image: "Foto",
	video: "Vídeo",
	audio: "Áudio",
	document: "Documento",
	sticker: "Figurinha",
};

const formatarDuracao = segundos => {
	const total = Number(segundos);
	if (!total || total < 0) return null;
	const minutos = Math.floor(total / 60);
	const resto = String(Math.floor(total % 60)).padStart(2, "0");
	return `${minutos}:${resto}`;
};

// Tickets.lastMessageMedia chega do backend como "<tipo>|<extra>" — ver
// backend/dist/helpers/DescreverMidiaTicket.js. Tickets antigos (antes da
// coluna) não têm o campo e caem no comportamento de texto puro.
const descreverUltimaMensagem = ticket => {
	const texto = ticket.lastMessage || "";
	const descricao = ticket.lastMessageMedia;
	/// ticket de antes do "contact|": o lastMessage ficou com o vCard cru ///
	if (!descricao && ehVCard(texto)) {
		return { Icone: ICONES_MIDIA.contact, texto: descreverContatos(lerVCards(texto)) };
	}
	if (!descricao) return { texto };

	const separador = descricao.indexOf("|");
	const tipo = separador === -1 ? descricao : descricao.slice(0, separador);
	const extra = separador === -1 ? "" : descricao.slice(separador + 1);
	const Icone = ICONES_MIDIA[tipo];
	if (!Icone) return { texto };

	if (tipo === "audio") {
		return { Icone, texto: formatarDuracao(extra) || ROTULOS_MIDIA.audio };
	}
	if (tipo === "document") {
		return { Icone, texto: extra || texto || ROTULOS_MIDIA.document };
	}
	if (tipo === "contact") {
		return { Icone, texto: extra || "Contato" };
	}
	// Foto/vídeo/figurinha: o lastMessage é a legenda quando difere do nome do
	// arquivo salvo (que é o que o extra carrega). O nomeArquivo é o que faz valer
	// para ticket antigo, cujo extra ficou com a URL pública inteira.
	const legenda = texto && texto !== nomeArquivo(extra) ? texto : null;
	return { Icone, texto: legenda || ROTULOS_MIDIA[tipo] };
};

const TicketListItem = ({ ticket, selectedItems, multiplaSelecao, onCheckboxChange, onMenuContexto }) => {
	const classes = useStyles();
	const history = useHistory();
	const { ticketId } = useParams();
	const { user } = useContext(AuthContext);
	const tagVisivel = useTagVisivel(ticket.tag ? ticket.tag.id : null);
	const tag = tagVisivel ? ticket.tag : null;
	const lastMessageDate = parseISO(ticket.dataUltimaMensagem);
	const now = new Date();

	const diffHours = differenceInHours(now, lastMessageDate);
	const diffMinutes = differenceInMinutes(now, lastMessageDate) % 60;
	const timeDifference =
		diffHours > 0
			? `${diffHours} hora${diffHours > 1 ? "s" : ""} e ${diffMinutes} minuto${diffMinutes > 1 ? "s" : ""}`
			: `${diffMinutes} minuto${diffMinutes > 1 ? "s" : ""}`;

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	const ultimaMensagem = descreverUltimaMensagem(ticket);
	const IconeUltimaMensagem = ultimaMensagem.Icone;

	const mostrarConexao = user?.whatsapps?.length > 1 && !!ticket.whatsapp;
	const mostrarAtendente = !!user?.gestor && !!ticket.user?.name;

	// Barras coloridas da lateral esquerda (etapa e etiqueta). Derivadas do próprio
	// ticket, não da aba, pra valerem em qualquer lista que renderize este item:
	//  - grupo não tem etapa nenhuma (ver CLAUDE.md), só etiqueta;
	//  - "Aguardando" é a fila de leads a distribuir — etapa e etiqueta ali não
	//    dizem nada, então nenhuma das duas barras aparece.
	const abaAguardando = !ticket.isGroup && ticket.status === "pending";
	const mostrarEtapa = !ticket.isGroup && !abaAguardando;
	const mostrarEtiqueta = !abaAguardando;

	// O NotificationsPopOver renderiza o item sem selectedItems/multiplaSelecao —
	// antes o `.includes` só rodava dentro do `multiplaSelecao &&`.
	const selecionado = (selectedItems || []).includes(ticket.id);

	// Um único title pra linha inteira (nome + ícones + conexão + atendente),
	// em vez de um por pedaço.
	const tituloLinhaPrincipal = [
		`Contato: ${ticket.contact?.name || ""}`,
		mostrarConexao ? `Conexão: ${ticket.whatsapp.name}` : null,
		mostrarAtendente ? `Atendente: ${ticket.user.name}` : null,
	]
		.filter(Boolean)
		.join("\n");

	return (
		<React.Fragment key={ticket.id}>
			{/* onContextMenu na linha toda (não só no ListItem) pra pegar também o
				clique com o direito em cima do checkbox. Sem o callback, o menu do
				navegador continua como era. */}
			<span
				className={clsx(classes.ticketRow, {
					[classes.ticketRowSelecionada]: multiplaSelecao && selecionado,
				})}
				onContextMenu={onMenuContexto ? e => onMenuContexto(e, ticket) : undefined}
			>
				{multiplaSelecao && (
					<span className={classes.selectCheckboxCell}>
						<Checkbox
							className={classes.selectCheckbox}
							color="primary"
							size="small"
							checked={selecionado}
							onChange={() => onCheckboxChange(ticket.id)}
							inputProps={{
								"aria-label": `Selecionar conversa de ${ticket.contact?.name || ""}`,
							}}
						/>
					</span>
				)}
				<ListItem
					dense
					button
					onClick={e => {
						// if (ticket.status === "pending") return;
						handleSelectTicket(ticket.id);
					}}
					selected={ticketId && +ticketId === ticket.id}
					style={{ height: "55px" }}
					className={clsx(classes.ticket, {
						[classes.pendingTicket]: ticket.status === "pending",
					})}
				>
					<span className={classes.marcadores}>
						{mostrarEtapa && (
							<Tooltip
								arrow
								placement="right"
								title={<span style={{ fontSize: 16 }}>{ticket.queue?.name || "Sem fila"}</span>}
							>
								<span
									style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
									className={classes.marcador}
								></span>
							</Tooltip>
						)}
						{mostrarEtiqueta && (
							<Tooltip
								arrow
								placement="right"
								title={<span style={{ fontSize: 16 }}>{tag?.descricao || "Sem etiqueta"}</span>}
							>
								{/* Sem etiqueta a pílula era transparente: o tooltip ficava
									pendurado num pedaço invisível. Agora vira trilho neutro. */}
								{/* Etiqueta sem cor é anterior à coluna Tags.cor: cai no cinza de
									antes, que ao menos marca que existe etiqueta. */}
								<span
									style={tag ? { backgroundColor: tag.cor || "var(--text-muted)" } : undefined}
									className={clsx(classes.marcador, {
										[classes.marcadorTrilho]: !tag,
									})}
								></span>
							</Tooltip>
						)}
						{!mostrarEtapa && !mostrarEtiqueta && (
							<span className={clsx(classes.marcador, classes.marcadorTrilho)} />
						)}
					</span>
					<ListItemAvatar>
						<AvatarContato contact={ticket?.contact} />
					</ListItemAvatar>
					<ListItemText
						disableTypography
						primary={
							<span className={classes.contactNameWrapper}>
								<span className={classes.contactNameBlock} title={tituloLinhaPrincipal}>
									<Typography
										noWrap
										component="span"
										variant="body2"
										color="textPrimary"
										className={classes.contactName}
									>
										{ticket.contact.name}
									</Typography>
									{mostrarConexao && (
										<Typography
											noWrap
											component="span"
											variant="body2"
											color="textPrimary"
											className={classes.contactMeta}
										>
											<SimCardIcon className={classes.inlineMetaIconWhats} />
											<strong>{ticket.whatsapp.name}</strong>
										</Typography>
									)}
									{mostrarAtendente && (
										<Typography
											noWrap
											component="span"
											variant="body2"
											color="textPrimary"
											className={clsx(classes.contactMeta, classes.contactMetaUser)}
										>
											<PersonIcon className={classes.inlineMetaIcon} />
											<strong>{ticket.user.name}</strong>
										</Typography>
									)}
								</span>
								{ticket.status === "closed" && (
									<Badge
										className={classes.closedBadge}
										badgeContent={"fechado"}
										color="primary"
									/>
								)}
								{ticket.lastMessage && (
									<Typography
										className={classes.lastMessageTime}
										component="span"
										variant="body2"
										color="textSecondary"
									>
										{isSameDay(parseISO(ticket.dataUltimaMensagem), new Date()) ? (
											<>{format(parseISO(ticket.dataUltimaMensagem), "HH:mm")}</>
										) : (
											<>{format(parseISO(ticket.dataUltimaMensagem), "dd/MM/yyyy")}</>
										)}
									</Typography>
								)}
								{/* {ticket.whatsappId && (
							<div className={classes.userTag} title={i18n.t("ticketsList.connectionTitle")}>CHIP {ticket.whatsappId}</div>
							)} */}
							</span>
						}
						secondary={
							<span className={classes.contactNameWrapper}>
								<Typography
									className={classes.contactLastMessage}
									noWrap
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{!ticket.isGroup && (
										<div>
											<div
												style={{
													display: "inline-block",
													border: "1px solid var(--status-green-fg)",
													backgroundColor: "var(--status-green-bg)",
													fontSize: "9px",
													padding: "2px 6px",
													marginRight: "5px",
													borderRadius: "4px",
													color: "var(--status-green-fg)",
													whiteSpace: "nowrap",
												}}
											>
												{ticket.receptivo ? (
													<span>Receptivo</span>
												) : (
													<span>Ativo</span>
												)}
											</div>
											{ticket.naoRespondido ? (
												<div
													style={{
														display: "inline-block",
														border: "1px solid var(--status-red-fg)",
														backgroundColor: "var(--status-red-bg)",
														fontSize: "9px",
														padding: "2px 6px",
														borderRadius: "4px",
														color: "var(--status-red-fg)",
														whiteSpace: "nowrap",
													}}
												>
													Aguardando há {timeDifference}
												</div>
											) : (
												<div
													style={{
														display: "inline-block",
														border: "1px solid var(--status-blue-fg)",
														backgroundColor: "var(--status-blue-bg)",
														fontSize: "9px",
														padding: "2px 6px",
														borderRadius: "4px",
														color: "var(--status-blue-fg)",
														whiteSpace: "nowrap",
													}}
												>
													Respondido há {timeDifference}
												</div>
											)}
										</div>
									)}
									{ticket.lastMessage ? (
										<span title={ultimaMensagem.texto}>
											{IconeUltimaMensagem ? (
												<>
													<IconeUltimaMensagem className={classes.lastMessageMediaIcon} />
													{ultimaMensagem.texto}
												</>
											) : (
												<MarkdownWrapper>
													{ultimaMensagem.texto.length > 40
														? `${ultimaMensagem.texto.substring(0, 40)}...`
														: ultimaMensagem.texto}
												</MarkdownWrapper>
											)}
										</span>
									) : (
										<br />
									)}
								</Typography>

								<Badge
									overlap="rectangular"
									className={classes.newMessagesCount}
									badgeContent={ticket.unreadMessages}
									classes={{
										badge: classes.badgeStyle,
									}}
								/>
							</span>
						}
					/>
					{user?.gestor && ticket.userId === user?.id && (
						<Tooltip
							arrow
							title={<span style={{ fontSize: 16 }}>Atribuído a mim</span>}
						>
							<span className={classes.assignedToMeCorner}>
								<span className={classes.assignedToMeCornerWhite} />
							</span>
						</Tooltip>
					)}
				</ListItem>
			</span>
			<Divider variant="inset" component="li" />
		</React.Fragment>
	);
};

export default TicketListItem;
